// SharedChart.jsx - Public view of shared bubble chart
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE, FRONTEND_URL } from '../../config/api';
import { httpRequest } from '../../utils/httpClient';
import ChartTabsLayout from '../common/ChartTabsLayout';
import BubbleChart from './BubbleChart';
import ActivityBubbleChart from './ActivityBubbleChart';
import { formatChartRangeForShare } from '../../utils/chartRangeFormatter';

function SharedChart() {
  const [searchParams] = useSearchParams();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = searchParams.get('user');
  const range = searchParams.get('range');
  const mode = searchParams.get('mode') || 'sessions';
  const type = searchParams.get('type');

  // Create minimal user object for golden borders
  const user = userId ? { id: parseInt(userId) } : null;

  useEffect(() => {
    const loadSharedData = async () => {
      if (!userId || !range || type !== 'sessions-chart') {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        const response = await httpRequest(`${API_BASE}/share/chart?user=${userId}&range=${range}&mode=${mode}&type=${type}`, {
          method: 'GET',
        });

        if (response.ok) {
          const data = await response.json();
          setChartData(data);
        } else {
          setError('Failed to load shared chart');
        }
      } catch (err) {
        setError('Error loading shared chart');
      } finally {
        setLoading(false);
      }
    };

    loadSharedData();
  }, [userId, range, mode, type]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--bg-primary, white)',
        color: 'var(--text-primary, #333)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <div style={{ fontSize: '1.2rem' }}>Loading shared chart...</div>
        </div>
      </div>
    );
  }

  if (error || !chartData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--bg-primary, white)',
        color: 'var(--text-primary, #333)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
          <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Chart not found</div>
          <div style={{ fontSize: '1rem', color: 'var(--text-secondary, #666)' }}>
            {error || 'This shared chart may have expired or is invalid'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-primary, white)',
      padding: '1rem',
      fontFamily: 'var(--font-family, system-ui, -apple-system, sans-serif)'
    }}>
      {/* Header */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '1rem',
        textAlign: 'center',
        borderBottom: '1px solid var(--border-color, #e0e0e0)',
        marginBottom: '2rem'
      }}>
        <h1 style={{
          fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
          color: 'var(--text-primary, #333)',
          margin: '0 0 0.5rem 0',
          fontWeight: '600'
        }}>
          🎮 Shared Gaming Stats
        </h1>
        <p style={{
          fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
          color: 'var(--text-secondary, #666)',
          margin: '0'
        }}>
          Last {formatChartRangeForShare(range, mode)} • {chartData.total_sessions} total sessions
        </p>
      </div>

      {/* Chart Container */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <ChartTabsLayout>
          {(activeTab) => (
            activeTab === "bubble" ? (
              <BubbleChart
                // The backend has already filtered chartData.timeline down to
                // the requested range/mode, so pass "max" here to avoid
                // re-filtering client-side with session-count semantics.
                timelineData={chartData.timeline}
                chartRange="max"
                user={user}
              />
            ) : (
              <ActivityBubbleChart
                timelineData={chartData.timeline}
                chartRange="max"
                user={user}
              />
            )
          )}
        </ChartTabsLayout>
      </div>

      {/* Footer */}
      <div style={{
        maxWidth: '1200px',
        margin: '2rem auto 0',
        padding: '1rem',
        textAlign: 'center',
        borderTop: '1px solid var(--border-color, #e0e0e0)',
        fontSize: '0.9rem',
        color: 'var(--text-secondary, #666)'
      }}>
        <p style={{ margin: '0' }}>
          Shared via Pista •
          <a
            href={FRONTEND_URL}
            style={{
              color: 'var(--pista-green-primary, #2E7D32)',
              textDecoration: 'none',
              marginLeft: '0.5rem'
            }}
          >
            Create your own gaming stats →
          </a>
        </p>
      </div>
    </div>
  );
}

export default SharedChart;
